import {DecimalPipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MessageService, SelectItem, SelectItemGroup} from 'primeng/api';
import {combineLatest, map} from 'rxjs';
import {LocalizedDatePipe} from '../components/LocalizedDatePipe';
import {MiniBookAdminService} from '../service/mini-book-admin.service';
import {Room} from '../shared/Room';
import {Timeslot} from '../shared/Timeslot';
import {TimetableEntry} from '../shared/TimetableEntry';
import {Tag} from '../shared/Tag';

@Component({
    selector: 'edu-udo-itmc-dev-mini-book-timetable-entry',
    templateUrl: './timetable-entry.component.html',
    styleUrls: ['./timetable-entry.component.css'],
    standalone: false
})
export class TimetableEntryComponent implements OnInit {

  timetableEntry!: TimetableEntry;

  types: String[] = [
    'INFO',
    'WORKSHOP',
    'BREAK'
  ];
  timeslotItems: SelectItem[] = [];
  roomItems: SelectItem[] = [];
  groupedTags: SelectItemGroup[] = [];
  tagCategories: any[] = [];
  tagMap: {[id: number]: Tag} = {};

  constructor(
    private adminService: MiniBookAdminService,
    private messageService: MessageService,
    private localizedDatePipe: LocalizedDatePipe,
    private decimalPipe: DecimalPipe,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit() {

    // Lädt bestehenden Eintrag, erstellt neuen oder klont einen vorhandenen Eintrag
    const routeSnapshot = this.route.snapshot;
    if (routeSnapshot.paramMap.has('id')) {
      const id = +routeSnapshot.paramMap.get('id')!;
      if (Number.isNaN(id)) {
        this.router.navigate(['timetableEntries']);
      } else if (id === 0) {
        if (routeSnapshot.queryParamMap.has('cloneId')) {
          const cloneId = +routeSnapshot.queryParamMap.get('cloneId')!;
          this.adminService.timetableEntries.getTimetableEntry(cloneId).subscribe({
            next: value => {
              this.timetableEntry = value;
              this.timetableEntry.id = 0;
            },
            error: () => {
              this.messageService.add({severity: 'error', summary: 'Stundenplaneintrag nicht gefunden'});
              this.router.navigate(['timetableEntries']);
            }
          });
        } else {
          this.timetableEntry = new TimetableEntry();
        }
      } else {
        this.adminService.timetableEntries.getTimetableEntry(id).subscribe({
          next: value => {
            this.timetableEntry = value;
          },
          error: () => {
            this.messageService.add({severity: 'error', summary: 'Stundenplaneintrag nicht gefunden'});
            this.router.navigate(['timetableEntries']);
          }
        });
      }
    }

    this.adminService.timeslots.getTimeslots().pipe(
      map<Timeslot[], SelectItem[]>(timeslots =>
        timeslots.map(timeslot => ({label: this.timeslotLabel(timeslot), value: timeslot.id}))
      )
    ).subscribe({
      next: value => {
        this.timeslotItems = value;
      },
      error: err => console.log(err)
    });

    this.adminService.rooms.getRooms().pipe(
      map<Room[], SelectItem[]>(rooms =>
        rooms.map(room => ({label: this.roomLabel(room), value: room.id}))
      )
    ).subscribe({
      next: value => {
        this.roomItems = value;
      },
      error: err => console.log(err)
    });

    combineLatest([
      this.adminService.tags.getTagCategories().pipe(
        map(categories => categories.filter(category => category.tagIds.length > 0))),
      this.adminService.tags.getTags().pipe(
        map(tags => {
          let tagMap: {[id: number]: Tag} = {};
          tags.forEach(tag => tagMap[tag.id] = tag);
          return tagMap;
        })
      )
    ]).subscribe({
      next: ([categories, tagMap]) => {
        this.tagCategories = categories;
        this.tagMap = tagMap;
        this.groupedTags = categories.map(category => ({
          label: category.label,
          value: category.id,
          items: category.tagIds
            .filter(id => tagMap[id])
            .map(id => ({label: tagMap[id].text, value: id}))
        }));
      },
      error: err => {
        this.groupedTags = [];
      }
    });
  }

  updateTimetableEntry() {
    if (this.timetableEntry.id !== 0) {
      this.adminService.timetableEntries.updateTimetableEntry(this.timetableEntry.id, this.timetableEntry).subscribe({
        next: value => {
          this.timetableEntry = value;
          this.messageService.add({summary: 'Änderungen gespeichert', severity: 'success'});
        },
        error: err => console.log(err)
      });
    } else {
      this.adminService.timetableEntries.createTimetableEntry(this.timetableEntry).subscribe({
        next: () => {
          this.router.navigate(['timetableEntries']);
          this.messageService.add({summary: 'Neuen Stundenplaneintrag hinzugefügt', severity: 'success'});
        },
        error: err => console.log(err)
      });
    }
  }

  showAttendeeList() {
    this.router.navigate(['timetableEntries', this.timetableEntry.id, 'attendees']);
  }

  cancel() {
    history.back();
  }

  private timeslotLabel(timeslot: Timeslot): string {
    let label = '';
    label += this.localizedDatePipe.transform(timeslot.date, 'dd.MM.yyyy');
    label += ', ';
    label += `${this.decimalPipe.transform(timeslot.start.hours, '2.0-0')}:${this.decimalPipe.transform(timeslot.start.minutes, '2.0-0')}`;
    label += ' - ';
    label += `${this.decimalPipe.transform(timeslot.end.hours, '2.0-0')}:${this.decimalPipe.transform(timeslot.end.minutes, '2.0-0')}`;
    label += ' Uhr';
    return label;
  }

  private roomLabel(room: Room): string {
    let label = '';
    label += room.name && room.name.trim().length > 0 ? room.name + ', ' : '';
    label += room.address && room.address.trim().length > 0 ? room.address + ', ' : '';
    label += room.floor && room.floor.trim().length > 0 ? room.floor + ', ' : '';
    label += room.room && room.room.trim().length > 0 ? room.room : '';
    return label
  }
}
